/** GPU Add Noise — fragment-shader port of
 *  `lib/filters/add-noise.ts`.
 *
 *  Single-pass shader. Per output pixel, derive integer (x, y) from
 *  v_uv (in CPU-aligned top-down convention so the hash inputs match
 *  the JS path's loop variables byte-for-byte), then run the same
 *  Wang-style integer hash the CPU uses. The hash is implemented
 *  with GLSL ES 3.00's native `uint` type and bitwise ops — `uint *
 *  uint` overflows to 32 bits identically to JS `Math.imul`, and
 *  XOR / shift have the same semantics, so the hash output is
 *  bit-identical to the CPU's.
 *
 *  Distribution + monochromatic + seed inputs mirror the CPU value
 *  shape exactly. Gaussian distribution uses Box-Muller with `log`
 *  and `cos`; transcendental precision is within FP32 vs FP64 noise
 *  (sub-byte difference in final output).
 *
 *  ── Precision boundary (uniform distribution) ────────────────
 *  The integer hash is bit-identical between JS and GPU, but the
 *  subsequent `float(uint)` cast in `uniformAt` truncates to FP32's
 *  24-bit mantissa, so uint values above 2^24 round to the nearest
 *  256. Absolute error on the normalized noise is bounded at ~3e-8;
 *  scaled by amount × 127, that becomes ~7.6e-6 in pixel value —
 *  three orders of magnitude under one byte. Invisible at any
 *  amount the UI exposes. JS does the same arithmetic in FP64 with
 *  full hash precision. RGBA32F intermediates would close the gap
 *  but cost 4× texture memory; not worth it for sub-LSB noise.
 *
 *  Identity short-circuit at `amount === 0` matches the JS path.
 *
 *  ── Y-axis convention ─────────────────────────────────────────
 *  Per-pixel hash inputs are integer top-down (x, y). The
 *  `(1.0 - v_uv.y)` flip in `yInt` puts y = 0 at DOM top (matching
 *  the CPU loop). Without it, every pixel would hash with the
 *  Y-mirrored row index and the noise pattern would land at the
 *  wrong row — visually still "noise" but byte-different from CPU
 *  in a way Re-Roll wouldn't reset. See twirl.ts header for the
 *  broader rationale. */

import type { AddNoiseValue } from '../../filters/add-noise';
import { applyIdentityCopy } from '../identity-copy';
import { applyShader } from '../runtime';

const ADD_NOISE_FRAG = `#version 300 es
precision highp float;
precision highp int;
in vec2 v_uv;
uniform sampler2D u_source;
uniform vec2 u_dims;
uniform int u_seed;       // signed-int reinterpretation of the JS seed; bits identical
uniform int u_seedG;      // seed ^ 0x55555555
uniform int u_seedB;      // seed ^ 0xaaaaaaaa
uniform float u_scale;    // (amount/100) * 127, in 0..255 domain
uniform int u_gauss;      // 0 = uniform, 1 = gaussian
uniform int u_mono;       // 0 = per-channel, 1 = monochromatic
out vec4 outColor;

// Wang-style 32-bit integer mix. Mirrors lib/filters/add-noise.ts:
//   h = (x*374761393) ^ (y*668265263) ^ seed
//   h = (h ^ (h>>13)) * 1274126177
//   h = h ^ (h>>16)
// uint multiplication wraps modulo 2^32 in WebGL2 (GLSL ES 3.00),
// which matches Math.imul + the |0 / >>> 0 dance the JS path uses.
uint hash32u(uint x, uint y, uint seed) {
  uint h = (x * 374761393u) ^ (y * 668265263u) ^ seed;
  h = (h ^ (h >> 13u)) * 1274126177u;
  h = h ^ (h >> 16u);
  return h;
}

// Map hash output to [-1, 1) uniform. The +0.5 / 4294967296.0
// shift makes the distribution symmetric around 0 (otherwise zero
// is twice as likely as -0.999...). Matches the CPU uniformAt.
float uniformAt(uint x, uint y, uint seed) {
  return (float(hash32u(x, y, seed)) + 0.5) / 4294967296.0 * 2.0 - 1.0;
}

// Box-Muller — two independent uniforms u1, u2 in (0, 1) yield one
// standard-normal sample. The +1 / 4294967297.0 shift keeps u1
// strictly positive so log(u1) is finite. Same constants as the
// CPU gaussianAt.
float gaussianAt(uint x, uint y, uint seed) {
  float u1 = (float(hash32u(x, y, seed)) + 1.0) / 4294967297.0;
  uint seed2 = seed ^ 0x9e3779b9u;
  float u2 = (float(hash32u(x, y, seed2)) + 1.0) / 4294967297.0;
  return sqrt(-2.0 * log(u1)) * cos(2.0 * 3.14159265358979 * u2);
}

void main() {
  // Integer pixel coords in CPU top-down convention.
  int xInt = int(floor(v_uv.x * u_dims.x));
  int yInt = int(floor((1.0 - v_uv.y) * u_dims.y));
  uint xu = uint(xInt);
  uint yu = uint(yInt);
  uint seedR = uint(u_seed);
  uint seedG = uint(u_seedG);
  uint seedB = uint(u_seedB);

  vec3 noise;
  if (u_gauss != 0) {
    float nR = clamp(gaussianAt(xu, yu, seedR), -3.0, 3.0) / 3.0;
    noise.r = nR;
    if (u_mono != 0) {
      noise.g = nR;
      noise.b = nR;
    } else {
      noise.g = clamp(gaussianAt(xu, yu, seedG), -3.0, 3.0) / 3.0;
      noise.b = clamp(gaussianAt(xu, yu, seedB), -3.0, 3.0) / 3.0;
    }
  } else {
    float nR = uniformAt(xu, yu, seedR);
    noise.r = nR;
    if (u_mono != 0) {
      noise.g = nR;
      noise.b = nR;
    } else {
      noise.g = uniformAt(xu, yu, seedG);
      noise.b = uniformAt(xu, yu, seedB);
    }
  }

  vec4 src = texture(u_source, v_uv);
  // CPU adds noise * scale in 0..255 then clamps via Uint8ClampedArray.
  // In 0..1 space: src + noise * scale / 255, clamped to [0, 1] by
  // the framebuffer's RGBA8 format on write.
  vec3 result = src.rgb + noise * u_scale / 255.0;
  outColor = vec4(clamp(result, 0.0, 1.0), src.a);
}
`;

export function applyAddNoiseOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: AddNoiseValue,
): boolean {
  const amount = Math.max(0, Math.min(100, value.amount));
  if (amount === 0) return applyIdentityCopy(source, target);
  const scale = (amount / 100) * 127;
  const seed = value.seed | 0;
  // Pre-compute the channel-offset seeds CPU-side using the same
  // XOR constants the JS path uses. Passing them as separate uniforms
  // (rather than computing them in-shader) keeps the shader's
  // integer arithmetic simple — and the JS-side `| 0` matches the
  // signed-32-bit reinterpretation GLSL does on int → uint.
  const seedG = (seed ^ 0x55555555) | 0;
  const seedB = (seed ^ 0xaaaaaaaa) | 0;
  return applyShader({
    source,
    target,
    fragShaderSrc: ADD_NOISE_FRAG,
    uniforms: {
      u_dims: { type: 'vec2', value: [source.width, source.height] },
      u_seed: { type: 'int', value: seed },
      u_seedG: { type: 'int', value: seedG },
      u_seedB: { type: 'int', value: seedB },
      u_scale: { type: 'float', value: scale },
      u_gauss: { type: 'int', value: value.distribution === 'gaussian' ? 1 : 0 },
      u_mono: { type: 'int', value: value.monochromatic ? 1 : 0 },
    },
  });
}
